import React, { useState } from 'react'
import {
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AnDateField from '../../../components/DateTime/DateField'
import API from '../../../services/API'

const SourceRestockForm = ({
  source,
  name,
  size,
  noExpiry,
  onRestocked,
  claim,
  onClaim,
  onRelease,
  currentUserId,
}) => {
  const [expiryDate, setExpiryDate] = useState(null)
  const [quantity, setQuantity] = useState(1)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [claiming, setClaiming] = useState(false)

  const claimedByMe =
    !!claim && String(claim.claimedBy?._id) === String(currentUserId)

  const handleClaimToggle = async () => {
    setClaiming(true)
    try {
      if (claimedByMe) await onRelease()
      else await onClaim()
    } finally {
      setClaiming(false)
    }
  }

  const handleAdd = async () => {
    setSubmitting(true)
    setError('')
    try {
      for (let i = 0; i < quantity; i++) {
        // eslint-disable-next-line no-await-in-loop
        await API.post('/makeready/quick-add-item', {
          callsignAssetID: source.trackedItemId,
          sectionName: source.sectionName,
          name,
          size,
          noExpiry,
          expiryDate: noExpiry ? null : expiryDate,
        })
      }
      onRestocked({ name, size, source, quantity })
      setQuantity(1)
      setExpiryDate(null)
    } catch (e) {
      console.error(e)
      setError('Failed to restock. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Stack gap={1} sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" fontWeight={600}>
          {source.label}
        </Typography>
        <Stack direction="row" alignItems="center" gap={1}>
          {claim && !claimedByMe && (
            <Chip
              size="small"
              variant="outlined"
              label={`Claimed by ${claim.claimedBy?.firstName ?? 'someone'}`}
            />
          )}
          {claimedByMe && (
            <Chip size="small" color="info" label="Claimed by you" />
          )}
          <Button size="small" disabled={claiming} onClick={handleClaimToggle}>
            {claimedByMe ? 'Release' : 'Claim'}
          </Button>
          <Typography variant="caption" color="error.main" fontWeight={700}>
            {source.missing} needed
          </Typography>
        </Stack>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ sm: 'center' }}>
        {!noExpiry && (
          <AnDateField
            size="small"
            label="Expiry Date"
            format="DD/MM/YYYY"
            value={expiryDate}
            onChange={setExpiryDate}
          />
        )}
        <Stack direction="row" alignItems="center" gap={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
          >
            <RemoveIcon fontSize="small" />
          </Button>
          <Typography minWidth={24} textAlign="center">
            {quantity}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setQuantity((q) => Math.min(source.missing, q + 1))}
          >
            <AddIcon fontSize="small" />
          </Button>
        </Stack>
        <Button
          size="small"
          variant="contained"
          color="success"
          disabled={submitting || (!noExpiry && !expiryDate)}
          onClick={handleAdd}
        >
          {submitting ? 'Adding...' : 'Restock'}
        </Button>
      </Stack>
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Stack>
  )
}

// One aggregated item row in the Restock Queue: total needed across the
// fleet, which vehicles/bags need it, and an inline restock action per
// source so an operative can knock items out without leaving the queue.
const RestockQueueRow = ({
  entry,
  onRestocked,
  claimsByKey,
  onClaim,
  onRelease,
  currentUserId,
}) => {
  const [expanded, setExpanded] = useState(false)

  if (entry.sources.length === 0) return null

  const claimKeyFor = (source) =>
    `${source.trackedItemId}|${source.sectionName}|${entry.name}|${entry.size}`

  return (
    <Paper elevation={1} sx={{ overflow: 'hidden' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ p: 1.5 }}
      >
        <Stack>
          <Typography fontWeight={700}>
            {entry.name} {entry.size && `(${entry.size})`}
          </Typography>
          <Stack direction="row" gap={0.5} flexWrap="wrap" mt={0.5}>
            {entry.sources.map((s) => (
              <Chip key={s.trackedItemId} size="small" label={`${s.label} · ${s.missing}`} />
            ))}
          </Stack>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1}>
          <Chip color="secondary" label={`${entry.total} needed`} sx={{ fontWeight: 700 }} />
          <IconButton size="small" onClick={() => setExpanded((prev) => !prev)}>
            <ExpandMoreIcon
              sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
            />
          </IconButton>
        </Stack>
      </Stack>
      <Collapse in={expanded}>
        <Divider />
        <Stack gap={1} sx={{ p: 1.5 }}>
          {entry.sources.map((source) => {
            const claimKey = claimKeyFor(source)
            return (
              <SourceRestockForm
                key={source.trackedItemId}
                source={source}
                name={entry.name}
                size={entry.size}
                noExpiry={entry.noExpiry}
                onRestocked={onRestocked}
                claim={claimsByKey?.get(claimKey)}
                onClaim={() => onClaim(claimKey)}
                onRelease={() => onRelease(claimKey)}
                currentUserId={currentUserId}
              />
            )
          })}
        </Stack>
      </Collapse>
    </Paper>
  )
}

export default RestockQueueRow
